"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import posthog from "posthog-js";

import { Button } from "@/components/ui/Button";
import { getStoredConsent, saveConsent } from "@/lib/cookie-consent";

// KAN-619 — CMP de cookies do site institucional.
// Aparece na primeira visita; persiste 6 meses (ou até versão do schema mudar).
// Toggle individual por categoria (analytics/marketing); "Essenciais" sempre on.
// Linka pra /privacidade.
//
// VOR-94 (2026-05-10) — Bea pediu mensagem mais discreta:
//   - Container compacto (buttons sm, copy explicando "cookies"
//     pra quem não conhece, link da política em linha separada)
//   - Modal sem subtitle redundante, "Essenciais" no lugar de
//     "Necessários", tag "Sempre" em vez de "Sempre ativo"
//   - Botão Salvar desabilitado quando o usuário não mudou nenhum
//     toggle em relação ao estado inicial
//   - Mobile: Cancelar fica abaixo de Salvar, ambos full-width
//
// VOR-94 follow-up (2026-05-10): Bea revisou o copy e a hierarquia:
//   - Texto inicial reescrito ("Guardamos algumas informações no seu
//     navegador (cookies) para manter o site funcionando e melhorar
//     sua experiência. Você pode escolher o que deseja permitir.")
//   - Buttons reordenados: Permitir tudo (primary) primeiro, depois
//     Manter essenciais (ghost) e Personalizar (ghost). Em mobile,
//     Permitir tudo ocupa a linha 1 sozinho e os outros 2 dividem a
//     linha 2.
//   - Modal: descrições reescritas, tag "Sempre" → "Ativo".
//   - Labels dos botões ajustados: "Apenas essenciais" → "Manter
//     essenciais", "Aceitar tudo" → "Permitir tudo".

const ANALYTICS_DEFAULT = true;
const MARKETING_DEFAULT = false;

export function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(ANALYTICS_DEFAULT);
  const [marketingEnabled, setMarketingEnabled] = useState(MARKETING_DEFAULT);

  useEffect(() => {
    if (!getStoredConsent()) setVisible(true);
  }, []);

  const applyConsent = (analytics: boolean, marketing: boolean) => {
    saveConsent(analytics, marketing);
    if (analytics) {
      posthog.opt_in_capturing();
    } else {
      posthog.opt_out_capturing();
    }
    setVisible(false);
    setCustomizeOpen(false);
  };

  if (!visible) return null;

  return (
    <>
      <div
        role="region"
        aria-label="Preferências de cookies"
        className="fixed bottom-0 left-0 right-0 z-50 border-t border-white/10 bg-canvas/95 px-4 py-3 backdrop-blur-md sm:px-6"
      >
        <div className="mx-auto flex max-w-5xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm text-white/80">
            <p>
              Guardamos algumas informações no seu navegador (cookies) para
              manter o site funcionando e melhorar sua experiência. Você pode
              escolher o que deseja permitir.
            </p>
            <p className="mt-1">
              Para saber mais, consulte nossa{" "}
              <Link
                href="/privacidade"
                className="font-medium text-accent underline-offset-2 hover:underline"
              >
                Política de Privacidade
              </Link>
              .
            </p>
          </div>
          {/*
            VOR-94 follow-up: layout dos buttons.
            - Mobile (default): "Permitir tudo" linha 1 sozinho (full-width),
              "Manter essenciais" e "Personalizar" linha 2 lado a lado.
            - Desktop (sm+): tudo numa linha só.
          */}
          <div className="flex shrink-0 flex-col gap-2 sm:flex-row sm:flex-wrap">
            <Button
              size="sm"
              onClick={() => applyConsent(true, true)}
              className="w-full sm:w-auto"
            >
              Permitir tudo
            </Button>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => applyConsent(false, false)}
                className="flex-1 sm:flex-none"
              >
                Manter essenciais
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCustomizeOpen(true)}
                className="flex-1 sm:flex-none"
              >
                Personalizar
              </Button>
            </div>
          </div>
        </div>
      </div>

      {customizeOpen ? (
        <CustomizeDialog
          analyticsEnabled={analyticsEnabled}
          marketingEnabled={marketingEnabled}
          onAnalyticsChange={setAnalyticsEnabled}
          onMarketingChange={setMarketingEnabled}
          onCancel={() => setCustomizeOpen(false)}
          onSave={() => applyConsent(analyticsEnabled, marketingEnabled)}
        />
      ) : null}
    </>
  );
}

function CustomizeDialog({
  analyticsEnabled,
  marketingEnabled,
  onAnalyticsChange,
  onMarketingChange,
  onCancel,
  onSave,
}: {
  analyticsEnabled: boolean;
  marketingEnabled: boolean;
  onAnalyticsChange: (v: boolean) => void;
  onMarketingChange: (v: boolean) => void;
  onCancel: () => void;
  onSave: () => void;
}) {
  const dirty =
    analyticsEnabled !== ANALYTICS_DEFAULT || marketingEnabled !== MARKETING_DEFAULT;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-customize-title"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 px-4 backdrop-blur-sm"
    >
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-canvas p-6 shadow-2xl">
        <h2
          id="cookie-customize-title"
          className="font-display text-xl text-white"
        >
          Preferências de cookies
        </h2>

        <div className="mt-5 space-y-5">
          <CategoryRow
            title="Essenciais"
            description="Mantêm o site funcionando com segurança, incluindo login, prevenção de fraudes e navegação."
            badge="Ativo"
          />
          <CategoryRow
            title="Analytics"
            description="Ajuda a entender como o site é usado para melhorarmos desempenho, navegação e conteúdo."
            toggle={{
              checked: analyticsEnabled,
              onChange: onAnalyticsChange,
              label: "Ativar analytics",
            }}
          />
          <CategoryRow
            title="Marketing"
            description="Permite personalizar conteúdos e comunicações de acordo com seus interesses."
            toggle={{
              checked: marketingEnabled,
              onChange: onMarketingChange,
              label: "Ativar marketing",
            }}
          />
        </div>

        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="ghost" size="sm" onClick={onCancel} className="w-full sm:w-auto">
            Cancelar
          </Button>
          <Button size="sm" onClick={onSave} disabled={!dirty} className="w-full sm:w-auto">
            Salvar preferências
          </Button>
        </div>
      </div>
    </div>
  );
}

function CategoryRow({
  title,
  description,
  badge,
  toggle,
}: {
  title: string;
  description: string;
  badge?: string;
  toggle?: {
    checked: boolean;
    onChange: (v: boolean) => void;
    label: string;
  };
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="text-sm font-semibold text-white">{title}</p>
        <p className="mt-0.5 text-xs text-white/60">{description}</p>
      </div>
      {badge ? (
        <span className="shrink-0 rounded-full bg-white/10 px-2 py-0.5 text-[11px] font-medium text-white/70">
          {badge}
        </span>
      ) : null}
      {toggle ? <Toggle {...toggle} /> : null}
    </div>
  );
}

function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-canvas ${
        checked ? "bg-accent" : "bg-white/20"
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
          checked ? "translate-x-4" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}
